// =============================================================================
// FE-05: Delegation Settings Page (Admin)
// PRD §4 Feature A — all 14 org-wide delegation settings
// Route: /admin/settings/delegation
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  Bell,
  Clock,
  Users,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

import { PageHeader } from '@/components/blocks/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { useDelegationService } from '../hooks/useDelegationService';
import type { DelegationSettings } from '../types/delegation.types';
import { usePermissions } from '@/contexts/PermissionContext';
import { DEFAULT_DELEGATION_SETTINGS } from '../types/delegation.types';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SettingsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border rounded-lg p-4 space-y-3 bg-background">
          <div className="h-5 w-40 bg-muted rounded animate-pulse" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[1, 2].map((j) => (
              <div key={j} className="border rounded-lg p-4 bg-background space-y-2">
                <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                <div className="h-3 w-64 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Permission card — matches the General Settings / Edit Role card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({
  id,
  label,
  description,
  disabled,
  disabledReason,
  control,
  onClick,
}: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg border bg-background transition-colors
        ${disabled ? 'opacity-60' : ''}
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        {disabled && disabledReason && (
          <p className="text-xs text-amber-600 mt-0.5">{disabledReason}</p>
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accordion section — matches General Settings / Edit Role accordion pattern
// ---------------------------------------------------------------------------

interface SectionProps {
  value: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Section({ value, title, description, icon, children }: SectionProps) {
  return (
    <AccordionItem value={value} className="border rounded-lg">
      <AccordionTrigger className="px-4 hover:no-underline">
        <div className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            {icon}
          </div>
          <div>
            <p className="font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">{description}</p>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pt-2 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {children}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const DelegationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { service: delegationService } = useDelegationService('approval-delegation');
  const { canManageApprovalWorkflows } = usePermissions();

  // Data state
  const [settings, setSettings] = useState<DelegationSettings>(DEFAULT_DELEGATION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // ---------------------------------------------------------------------------
  // Load settings
  // ---------------------------------------------------------------------------

  const loadSettings = useCallback(async () => {
    try {
      setLoadError(null);
      setLoading(true);
      const data = await delegationService.getDelegationSettings();
      setSettings(data);
      setHasUnsavedChanges(false);
    } catch {
      setLoadError('Failed to load delegation settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [delegationService]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function patch<K extends keyof DelegationSettings>(key: K, value: DelegationSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await delegationService.updateDelegationSettings(settings);
      setSettings(updated);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      toast.success('Delegation settings saved', {
        description: 'Your changes have been applied organisation-wide.',
      });
    } catch {
      toast.error('Failed to save settings', {
        description: 'Please check your inputs and try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    }
  };

  const handleConfirmDiscard = async () => {
    setShowDiscardDialog(false);
    await loadSettings();
    toast.info('Changes discarded');
  };

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Render — access denied
  // ---------------------------------------------------------------------------

  if (!canManageApprovalWorkflows()) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <Alert variant="destructive">
          <AlertDescription>
            You don't have permission to manage approval settings. Contact your administrator to request access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — loading
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          size={36}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <SettingsSkeleton />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — error
  // ---------------------------------------------------------------------------

  if (loadError) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          size={36}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>{loadError}</span>
            <Button variant="outline" size="sm" onClick={loadSettings}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — main
  // ---------------------------------------------------------------------------

  const delegationDisabled = !settings.delegationEnabled;

  return (
    <div className="space-y-6 pb-24">
      {/* Page header */}
      <PageHeader
        title="Delegation Settings"

        showBreadcrumb={false}
        size={36}
        icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings/approvals')}
            >
              Approvals Settings
            </Button>
          </div>
        }
      />

      {/* Unsaved changes banner */}
      {hasUnsavedChanges && (
        <Alert variant="warning">
          <AlertDescription>
            You have unsaved changes. Save them using the button below or they will be lost.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Master Switch ─────────────────────────────────────────────── */}
      <Card className={`border-2 ${settings.delegationEnabled ? 'border-primary/20' : 'border-border'}`}>
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Label
                  htmlFor="delegationEnabled"
                  className="text-base font-semibold cursor-pointer select-none"
                >
                  Enable Approval Delegation
                </Label>
                {settings.delegationEnabled ? (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0 text-xs">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    Disabled
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Master switch for the entire delegation feature. When turned off, managers cannot
                create new delegations and all delegation UI is hidden across the application.
                Existing active delegations are not automatically revoked.
              </p>
            </div>
            <Checkbox
              id="delegationEnabled"
              checked={settings.delegationEnabled}
              onCheckedChange={(val) => patch('delegationEnabled', !!val)}
              className="shrink-0 mt-0.5"
            />
          </div>
        </CardContent>
      </Card>

      {/* Disabled overlay hint */}
      {delegationDisabled && (
        <Alert>
          <AlertDescription className="text-sm">
            Delegation is currently <strong>disabled</strong> organisation-wide. The settings
            below are preserved but will not take effect until delegation is re-enabled.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Accordion sections ───────────────────────────────────────── */}
      <Accordion
        type="multiple"
        defaultValue={['eligibility', 'duration', 'form', 'notifications']}
        className="space-y-4"
      >
        {/* ── Section 1: Eligibility & Flow ─────────────────────────── */}
        <Section
          value="eligibility"
          title="Eligibility & Flow"
          description="Control who can be a delegate and how delegations behave"
          icon={<Users className="h-4 w-4 text-primary" />}
        >
          <SettingCard
            id="eligibilityRequireApprovalPermission"
            label="Require delegate to have approval permissions"
            description="When enabled, only employees who already hold approval permissions can be selected as delegates. Recommended to keep this on for compliance."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="eligibilityRequireApprovalPermission"
                checked={settings.eligibilityRequireApprovalPermission}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('eligibilityRequireApprovalPermission', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('eligibilityRequireApprovalPermission', !settings.eligibilityRequireApprovalPermission)}
          />

          <SettingCard
            id="allowSelfDelegation"
            label="Allow self-delegation"
            description="Allow a manager to delegate approval to themselves. This should almost always remain off."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="allowSelfDelegation"
                checked={settings.allowSelfDelegation}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('allowSelfDelegation', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('allowSelfDelegation', !settings.allowSelfDelegation)}
          />
        </Section>

        {/* ── Section 2: Duration & Overlap Rules ───────────────────── */}
        <Section
          value="duration"
          title="Duration & Overlap Rules"
          description="Set limits on how long delegations can run and whether they can overlap"
          icon={<Clock className="h-4 w-4 text-primary" />}
        >
          <SettingCard
            id="maxDelegationDays"
            label="Maximum delegation duration"
            description="The longest a single delegation can run. Managers cannot create delegations that exceed this limit."
            disabled={delegationDisabled}
            control={
              <div className="flex items-center gap-2">
                <Input
                  id="maxDelegationDays"
                  type="number"
                  min={1}
                  max={365}
                  value={settings.maxDelegationDays}
                  disabled={delegationDisabled}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    if (!isNaN(parsed)) patch('maxDelegationDays', Math.min(365, Math.max(1, parsed)));
                  }}
                  className="w-20 h-8 text-sm text-center"
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">days</span>
              </div>
            }
          />

          <SettingCard
            id="allowOverlappingDelegations"
            label="Allow overlapping delegations"
            description="When enabled, a manager can have multiple active delegations with overlapping date ranges."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="allowOverlappingDelegations"
                checked={settings.allowOverlappingDelegations}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('allowOverlappingDelegations', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('allowOverlappingDelegations', !settings.allowOverlappingDelegations)}
          />

          <SettingCard
            id="allowCascadeDelegation"
            label="Allow cascade delegation"
            description="Allow a delegate to further delegate their inherited approval responsibilities to another person."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="allowCascadeDelegation"
                checked={settings.allowCascadeDelegation}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('allowCascadeDelegation', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('allowCascadeDelegation', !settings.allowCascadeDelegation)}
          />
        </Section>

        {/* ── Section 3: Form Requirements ──────────────────────────── */}
        <Section
          value="form"
          title="Form Requirements"
          description="Customise what managers must provide when creating a delegation"
          icon={<ShieldCheck className="h-4 w-4 text-primary" />}
        >
          <SettingCard
            id="requireNotes"
            label="Require notes on delegation"
            description="Force managers to provide a reason or notes when creating a delegation. Useful for audit trails."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="requireNotes"
                checked={settings.requireNotes}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('requireNotes', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('requireNotes', !settings.requireNotes)}
          />
        </Section>

        {/* ── Section 4: Notifications ──────────────────────────────── */}
        <Section
          value="notifications"
          title="Notifications"
          description="Control which automated email notifications are sent during the delegation lifecycle"
          icon={<Bell className="h-4 w-4 text-primary" />}
        >
          <SettingCard
            id="notifyDelegateOnAssignment"
            label="Notify delegate on assignment"
            description="Send an email to the delegate when they are assigned as an approval delegate."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="notifyDelegateOnAssignment"
                checked={settings.notifyDelegateOnAssignment}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('notifyDelegateOnAssignment', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('notifyDelegateOnAssignment', !settings.notifyDelegateOnAssignment)}
          />

          <SettingCard
            id="notifyDelegateOnStart"
            label="Notify delegate on delegation start"
            description="Send a reminder email to the delegate on the day their delegation period begins."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="notifyDelegateOnStart"
                checked={settings.notifyDelegateOnStart}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('notifyDelegateOnStart', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('notifyDelegateOnStart', !settings.notifyDelegateOnStart)}
          />

          <SettingCard
            id="notifyManagerOnDelegateAction"
            label="Notify manager when delegate takes action"
            description="Send the manager an email each time their delegate approves or rejects a timesheet on their behalf."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="notifyManagerOnDelegateAction"
                checked={settings.notifyManagerOnDelegateAction}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('notifyManagerOnDelegateAction', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('notifyManagerOnDelegateAction', !settings.notifyManagerOnDelegateAction)}
          />

          {/* Progressive disclosure — expiry reminder + lead time */}
          <SettingCard
            id="notifyManagerBeforeExpiry"
            label="Notify manager before delegation expires"
            description="Send the manager a heads-up email before their delegation expires so they can renew it if needed."
            disabled={delegationDisabled}
            control={
              <Checkbox
                id="notifyManagerBeforeExpiry"
                checked={settings.notifyManagerBeforeExpiry}
                disabled={delegationDisabled}
                onCheckedChange={(val) => patch('notifyManagerBeforeExpiry', !!val)}
              />
            }
            onClick={delegationDisabled ? undefined : () => patch('notifyManagerBeforeExpiry', !settings.notifyManagerBeforeExpiry)}
          />

          {settings.notifyManagerBeforeExpiry && (
            <SettingCard
              id="expiryReminderHoursBefore"
              label="Expiry reminder lead time"
              description="How many hours before expiry the reminder email is sent to the manager."
              disabled={delegationDisabled}
              control={
                <div className="flex items-center gap-2">
                  <Input
                    id="expiryReminderHoursBefore"
                    type="number"
                    min={1}
                    max={168}
                    value={settings.expiryReminderHoursBefore}
                    disabled={delegationDisabled}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      if (!isNaN(parsed)) patch('expiryReminderHoursBefore', Math.min(168, Math.max(1, parsed)));
                    }}
                    className="w-20 h-8 text-sm text-center"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">hours before</span>
                </div>
              }
            />
          )}
        </Section>
      </Accordion>

      {/* Last saved indicator */}
      {lastSaved && !hasUnsavedChanges && (
        <p className="text-xs text-muted-foreground text-center">
          Last saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
        </p>
      )}

      {/* ── Sticky footer ─────────────────────────────────────────────── */}
      <div className="fixed bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ left: 'var(--sidebar-width)', right: 0 }}>
        <div className="flex h-16 items-center justify-between gap-4 px-6 max-w-[2000px] mx-auto">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {lastSaved && !hasUnsavedChanges && (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                Saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1.5 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleCancel}
              disabled={isSaving || !hasUnsavedChanges}
              className="min-w-[120px]"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Discard
            </Button>
            <Button
              size="lg"
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="min-w-[120px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Discard confirmation dialog ───────────────────────────────── */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              All unsaved changes to delegation settings will be lost and reverted to the last
              saved state. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
